import { useState } from "react";
import { AuthPageLayout } from "./AuthPageLayout";
import { LoadingButton } from "./LoadingButton";
import { CodeInputField } from "./CodeInputField";
import { PasswordRequirementsHint } from "./PasswordRequirementsHint";
import { PasswordField } from "./PasswordField";

/**
 * パスワードリセット: コード+新パスワード入力 UI
 */
export const ResetPasswordUI: React.FC<{
  loading: boolean;
  error: string | null;
  recoverySuggestion: string | null;
  email: string;
  /**
   * PRODUCT_DEV-891 入力欄の呼び方（例「メールアドレスまたは行員番号」）。
   * 独自ID で進んできた人の値が入るので、欄を `type="email"` にしない（ブラウザが送信を止める）。
   */
  idLabel?: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
  onEmailChange: (value: string) => void;
  onCodeChange: (value: string) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  // PRODUCT_DEV-498 URL ディープリンク由来で prefill された値のみロックする。
  // フィールド自身の現在値 (!!email / !!code) で disabled にすると、手入力1文字目で
  // 即 disabled になり iPad でキーボードが閉じて2桁目以降が打てなくなるため。
  emailLocked?: boolean;
  codeLocked?: boolean;
}> = ({
  loading,
  error,
  recoverySuggestion,
  email,
  idLabel = "メールアドレス",
  code,
  newPassword,
  confirmPassword,
  onEmailChange,
  onCodeChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onBack,
  emailLocked = false,
  codeLocked = false,
}) => {
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <AuthPageLayout>
      {/* タイトル */}
      <h2 className="text-3xl font-bold text-center text-neutral-900 mb-4">
        新しいパスワードを設定
      </h2>
      <p className="text-center text-neutral-600 mb-8">
        メールに届いたコードと新しいパスワードを入力してください。
      </p>

      {/* フォーム */}
      <form onSubmit={onSubmit} className="space-y-6">
        {/* メールアドレス */}
        <div>
          <label htmlFor="reset-email" className="block text-sm font-medium text-neutral-700 mb-2">
            {idLabel}
          </label>
          <input
            id="reset-email"
            type="text"
            autoComplete="username"
            placeholder={idLabel}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="w-full px-6 py-4 border border-neutral-300 rounded-2xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-primary transition-colors bg-neutral-50"
            disabled={loading || emailLocked}
            required
          />
        </div>

        {/* 検証コード */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            検証コード
          </label>
          <CodeInputField
            value={code}
            onChange={onCodeChange}
            disabled={loading || codeLocked}
            placeholder="6桁のコードを入力"
          />
        </div>

        <PasswordField
          id="reset-new-password"
          name="new-password"
          autoComplete="new-password"
          label="新しいパスワード"
          placeholder="新しいパスワードを入力"
          value={newPassword}
          onChange={onNewPasswordChange}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          disabled={loading}
          autoFocus={!!code}
        />

        <PasswordField
          id="reset-confirm-password"
          name="confirm-password"
          autoComplete="new-password"
          label="パスワード確認"
          placeholder="もう一度入力してください"
          value={confirmPassword}
          onChange={onConfirmPasswordChange}
          show={showConfirm}
          onToggle={() => setShowConfirm((v) => !v)}
          disabled={loading}
        />

        <PasswordRequirementsHint />

        {/* エラーメッセージ */}
        {error && <div className="text-sm text-red-600">{error}</div>}
        {recoverySuggestion && (
          <div className="text-sm text-neutral-700">{recoverySuggestion}</div>
        )}

        {/* ボタン */}
        <div className="space-y-3">
          <LoadingButton loading={loading} loadingMessage="設定中..." label="パスワードを設定する" />

          <button
            type="button"
            onClick={onBack}
            className="w-full bg-gray-500 hover:bg-gray-600 text-white py-4 rounded-2xl text-xl font-bold transition-colors"
            disabled={loading}
          >
            戻る
          </button>
        </div>
      </form>
    </AuthPageLayout>
  );
};
